import type { v2 } from '@sap/ux-specification-types';
import { pageLayoutInformation, syncRule, validity } from '../../../common/decoration/decorators';
import { getOVPsettingPath } from '../../application';
import type { ListCard, AnalyticalCard, StackCard, LinklistCard, TableCard, CustomCard } from '../controls/Card';
import type { RefreshStrategiesPropDef, ResizableLayoutVariant } from '@ui5/manifest/types/manifest';

/**
 * Imports date settings fields for the Overview Page.
 *
 * @param manifestSection - The manifest section containing date settings.
 * @param configPart - The configuration part to populate.
 */
function importDateSettingsFieldsOVP(manifestSection: object, configPart: any): void {
    for (const propertyKey in manifestSection['dateSettings']['fields']) {
        configPart.fields[propertyKey] = manifestSection['dateSettings']['fields'][propertyKey];
        const selectedValues = manifestSection['dateSettings']['fields'][propertyKey].selectedValues;
        if (typeof selectedValues === 'string') {
            configPart.fields[propertyKey].selectedValues = selectedValues.split(',') as v2.DateRangeType[];
        }
        for (const filterKey in manifestSection['dateSettings']['fields'][propertyKey].filter) {
            const filterValue = manifestSection['dateSettings']['fields'][propertyKey].filter[filterKey];
            configPart.fields[propertyKey].filter[filterKey] = filterValue;
            if (filterValue.equals && typeof filterValue.equals === 'string') {
                configPart.fields[propertyKey].filter[filterKey].equals = filterValue.equals.split(
                    ','
                ) as v2.DateRangeType[];
            }
        }
    }
}

/**
 * Imports date settings for the Overview Page.
 *
 * @param manifestSection - The manifest section containing date settings.
 * @returns The imported date settings as a `v2.DateRange` object.
 */
export function importDateSettingsOVP(manifestSection: object): v2.DateRange {
    if (!manifestSection || !manifestSection['dateSettings']) {
        return undefined;
    }
    const configPart: v2.DateRange = JSON.parse(JSON.stringify(manifestSection['dateSettings']));
    // Convert CSV format to an array of strings
    if (typeof manifestSection['dateSettings']['selectedValues'] === 'string') {
        configPart.selectedValues = manifestSection['dateSettings']['selectedValues'].split(',') as v2.DateRangeType[];
    }
    if (manifestSection['dateSettings']['fields']) {
        importDateSettingsFieldsOVP(manifestSection, configPart);
    }
    return configPart;
}

/**
 * Imports filter settings for the Overview Page.
 *
 * @param manifestSection - The manifest section containing filter settings.
 * @returns The imported filter settings as an object.
 */
export function importFilterSettings(manifestSection: object): object {
    const configPart = {};
    configPart['dateSettings'] = importDateSettingsOVP(manifestSection['filterSettings']);
    return configPart;
}

/**
 * Exports filter fields for the Overview Page.
 *
 * @param configPart - The configuration part containing filter settings.
 * @param key - The key for the filter settings.
 * @param manifestSection - The manifest section to populate.
 */
function exportFilterFields(configPart: v2.FilterSettingsType, key: string, manifestSection: unknown) {
    for (const fieldName in configPart['filterSettings'][key]['fields']) {
        const field = configPart['filterSettings'][key]['fields'][fieldName];
        if (field.selectedValues) {
            manifestSection['filterSettings'][key]['fields'][fieldName].selectedValues = field.selectedValues.join();
        }
        if (field.filter) {
            for (const filterKey in field.filter) {
                const filterValue = field.filter[filterKey];
                if (filterValue.equals && Array.isArray(filterValue.equals)) {
                    manifestSection['filterSettings'][key]['fields'][fieldName].filter[filterKey].equals =
                        filterValue.equals.join();
                }
            }
        }
    }
}

/**
 * Exports filter settings for the Overview Page.
 *
 * @param manifestSection - The manifest section to populate.
 * @param configPart - The configuration part containing filter settings.
 */
export function exportFilterSettings(manifestSection: object, configPart: v2.FilterSettingsType): void {
    if (!manifestSection['filterSettings']) {
        manifestSection['filterSettings'] = {};
    }
    const key = 'dateSettings';
    if (configPart['filterSettings'][key] === undefined) {
        delete manifestSection['filterSettings'][key];
    } else {
        manifestSection['filterSettings'][key] = JSON.parse(JSON.stringify(configPart['filterSettings'][key]));
        // Convert enum array to joined string
        if (configPart['filterSettings'][key]['selectedValues']) {
            manifestSection['filterSettings'][key]['selectedValues'] =
                configPart['filterSettings'][key]['selectedValues'].join();
        }
        if (configPart['filterSettings'][key]['fields']) {
            exportFilterFields(configPart, key, manifestSection);
        }
    }
}

type CardClassType = ListCard | AnalyticalCard | StackCard | LinklistCard | TableCard | CustomCard;

/**
 * Represents the Overview Page configuration.
 */
@pageLayoutInformation({
    id: 'sap.suite.ui.generic.template.OverviewPage.view.Details',
    pageId: 'sap.suite.ui.generic.template.OverviewPage'
})
export class OverviewPage implements v2.OverviewPageConfigV2 {
    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    globalFilterModel?: string;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    globalFilterEntityType?: string;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    globalFilterEntitySet?: string;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    showBasicSearch?: boolean;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    disableErrorPage?: boolean;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    smartVariantRequired?: boolean;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    bHeaderExpanded?: boolean;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    containerLayout?: v2.ContainerLayoutType;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    showDateInRelativeFormat?: boolean;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    disableTableCardFlexibility?: boolean;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    enableLiveFilter?: boolean;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    enableLazyRendering?: boolean;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    refreshStrategyOnAppRestore?: RefreshStrategiesPropDef;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    considerAnalyticalParameters?: boolean;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    refreshIntervalInMinutes?: number;

    @syncRule({
        manifest: { path: (): string => getOVPsettingPath() }
    })
    useDateRangeType?: boolean;

    chartSettings?: v2.ChartSettingsType;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath(),
            import: importFilterSettings,
            export: exportFilterSettings
        }
    })
    @validity({
        since: '1.80.0'
    })
    filterSettings?: v2.FilterSettingsType;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    dataLoadSettings?: v2.DataLoadSettingsType;

    @syncRule({
        manifest: {
            path: (): string => getOVPsettingPath()
        }
    })
    resizableLayout?: {
        [k: string]: ResizableLayoutVariant;
    };

    cards: {
        [k: string]: CardClassType;
    };
}
